/**
 * src/admin-ui/src/components/ErrorBoundary.tsx
 *
 * Application-level error fallback.
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Typography } from '@mui/material';
import {
  CenteredMessage,
  ErrorCard,
  ErrorDetails,
  ErrorPageContainer,
  FullScreenState,
  LargeErrorIcon,
  ReloadButton,
} from './utility.styles';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <ErrorPageContainer maxWidth="sm">
          <FullScreenState>
            <ErrorCard elevation={8}>
              <LargeErrorIcon />
              <Typography variant="h4" component="h1" gutterBottom>
                Произошла ошибка
              </Typography>
              <CenteredMessage variant="body1" color="text.secondary">
                При загрузке приложения произошла ошибка. Возможно, проблема с подключением к серверу.
              </CenteredMessage>
              
              {this.state.error && (
                <ErrorDetails>
                  <Typography variant="body2" fontFamily="monospace">
                    {this.state.error.message}
                  </Typography>
                </ErrorDetails>
              )}

              <ReloadButton
                variant="contained"
                size="large"
                onClick={this.handleReload}
              >
                Перезагрузить страницу
              </ReloadButton>

              <Typography variant="body2" color="text.secondary" align="center">
                Если проблема повторяется, проверьте настройки сервера
              </Typography>
            </ErrorCard>
          </FullScreenState>
        </ErrorPageContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 